import React from 'react';
import {
  ArrowUpRight, CookingPot, UtensilsCrossed, Sparkles, Zap, Flame, SprayCan,
  Soup, Package, Shirt, Microwave,
} from 'lucide-react';
import { categories, premiumCollections } from '../mock';
import useReveal from '../hooks/useReveal';

const iconMap = {
  CookingPot, UtensilsCrossed, Sparkles, Zap, Flame, SprayCan, Soup,
  Package, Shirt, Microwave,
};

// Decorative dotted pattern background for tiles
const TilePattern = ({ color = '#F58220' }) => (
  <svg
    className="absolute inset-0 w-full h-full opacity-[0.08] pointer-events-none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <pattern id={`dots-${color}`} width="16" height="16" patternUnits="userSpaceOnUse">
        <circle cx="2" cy="2" r="1.4" fill={color} />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#dots-${color})`} />
  </svg>
);

const Collections = () => {
  useReveal();
  return (
    <section id="collections" className="relative py-16 lg:py-24 paper-bg">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Top categories */}
        <div className="text-center mb-12 reveal">
          <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[#F58220] mb-4">
            Curated For You
          </div>
          <h2 className="h-display uppercase text-[40px] md:text-[64px] text-[#1a1410]">
            Crafted for the <span className="text-[#F58220]">Modern Home</span>
          </h2>
          <p className="mt-5 text-[15px] text-[#4D4D4D] max-w-[640px] mx-auto leading-relaxed">
            A thoughtful selection across four signature categories — from
            heirloom cookware to next-gen appliances.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
          {categories.map((c, i) => {
            const Icon = iconMap[c.icon] || CookingPot;
            const isOrange = c.tint === '#F58220';
            return (
              <div
                key={c.id}
                className={`reveal group relative rounded-2xl overflow-hidden aspect-[4/4.2] card-lift border ${
                  isOrange
                    ? 'bg-[#1a1410] text-[#F7F4EA] border-[#1a1410]'
                    : 'bg-[#F7F4EA] text-[#1a1410] border-[#E9D6C1]'
                }`}
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                <TilePattern color={isOrange ? '#F58220' : '#4D4D4D'} />
                <div className="relative h-full p-7 flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:rotate-6"
                      style={{
                        background: isOrange ? '#F58220' : '#F58220',
                        boxShadow: '0 12px 28px -12px rgba(245,130,32,0.45)',
                      }}
                    >
                      <Icon className="w-8 h-8 text-white" strokeWidth={1.6} />
                    </div>
                    <div
                      className={`text-[10px] uppercase tracking-[0.28em] font-bold ${
                        isOrange ? 'text-[#F58220]' : 'text-[#F58220]'
                      }`}
                    >
                      Category
                    </div>
                  </div>

                  <div>
                    <div className="h-display uppercase text-[30px] leading-none">{c.name}</div>
                    <div
                      className={`text-[13px] mt-3 ${
                        isOrange ? 'text-[#F7F4EA]/75' : 'text-[#4D4D4D]'
                      }`}
                    >
                      {c.desc}
                    </div>

                    <div className="mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold">
                      <span>Explore</span>
                      <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-500" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Premium Collections */}
        <div className="text-center mb-14 reveal">
          <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[#F58220] mb-4">
            Premium Collections
          </div>
          <h2 className="h-display uppercase text-[40px] md:text-[64px] text-[#1a1410]">
            Explore Our <span className="text-[#F58220]">Collections</span>
          </h2>
          <p className="mt-5 text-[15px] text-[#4D4D4D]">
            Visit our showrooms for a hands-on pick.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {premiumCollections.map((p, i) => {
            const Icon = iconMap[p.icon] || CookingPot;
            return (
              <div
                key={p.id}
                className="reveal group bg-white rounded-2xl overflow-hidden card-lift border border-[#E9D6C1] relative"
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div className="aspect-[4/3] relative overflow-hidden bg-gradient-to-br from-[#F7F4EA] to-[#E9D6C1]/60 flex items-center justify-center">
                  <TilePattern color="#F58220" />
                  <div className="relative w-20 h-20 rounded-2xl bg-white shadow-md flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-transform duration-500 border border-[#E9D6C1]">
                    <Icon className="w-10 h-10 text-[#F58220]" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="p-5 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-[16px] text-[#1a1410]">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8E8E8E] mt-1">
                      Collection
                    </div>
                  </div>
                  <div className="w-9 h-9 rounded-full bg-[#F7F4EA] flex items-center justify-center group-hover:bg-[#F58220] transition-colors duration-500">
                    <ArrowUpRight className="w-4 h-4 text-[#F58220] group-hover:text-white transition-colors duration-500" />
                  </div>
                </div>
              </div>
            );
          })}
          <a
            href="#products"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#products')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="reveal group rounded-2xl overflow-hidden bg-[#1a1410] text-[#F7F4EA] flex flex-col items-center justify-center aspect-[4/3] p-6 card-lift relative"
          >
            <TilePattern color="#F58220" />
            <div className="relative text-[11px] uppercase tracking-[0.3em] font-semibold text-[#F58220]">Discover</div>
            <div className="relative h-display uppercase text-[22px] mt-2">View More</div>
            <ArrowUpRight className="relative w-5 h-5 mt-4 group-hover:rotate-12 transition-transform duration-500" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Collections;
